import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import { useSettings } from '../hooks/useSettings';
import type { DataExportRequest } from '../types';

export function DataExportSettings() {
  const { requestDataExport, requestAccountDeletion, cancelAccountDeletion, isLoading, error, clearError } =
    useSettings(null);

  const [exportResult, setExportResult] = useState<DataExportRequest | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletionRequested, setDeletionRequested] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const handleRequestExport = async () => {
    setSuccess(null);
    try {
      const result = await requestDataExport();
      setExportResult(result);
      setSuccess('Exportacao de dados solicitada. Você recebera um email quando estiver pronta.');
    } catch {
      // error handled by hook
    }
  };

  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== 'EXCLUIR MINHA CONTA') return;

    try {
      await requestAccountDeletion(deletePassword);
      setDeletionRequested(true);
      setDeleteDialogOpen(false);
      setDeletePassword('');
      setDeleteConfirmText('');
    } catch {
      // error handled by hook
    }
  };

  const handleCancelDeletion = async () => {
    try {
      await cancelAccountDeletion();
      setDeletionRequested(false);
      setSuccess('Solicitacao de exclusao cancelada.');
    } catch {
      // error handled by hook
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Data Export */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Exportacao de Dados (LGPD/GDPR)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Solicite uma copia de todos os seus dados pessoais. O arquivo JSON sera
            entregue em ate 72 horas e inclui: perfil, viagens, pins, metadados de
            fotos e interacoes sociais.
          </Typography>

          {exportResult && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Status: {exportResult.status} | Solicitado em:{' '}
              {new Date(exportResult.requestedAt).toLocaleDateString('pt-BR')}
              {exportResult.downloadUrl && (
                <Button
                  href={exportResult.downloadUrl}
                  size="small"
                  sx={{ ml: 2 }}
                  startIcon={<DownloadIcon />}
                >
                  Baixar
                </Button>
              )}
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={handleRequestExport}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
          >
            Solicitar exportacao
          </Button>
        </CardContent>
      </Card>

      {/* Account Deletion */}
      <Card sx={{ border: '1px solid', borderColor: 'error.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6" fontWeight={600} color="error">
              Exclusao de Conta
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ao solicitar a exclusao, sua conta entrara em um período de carencia de
            30 dias. Apos esse período, todos os seus dados pessoais serao
            permanentemente removidos em conformidade com LGPD/GDPR. Você pode
            cancelar a exclusao durante o período de carencia.
          </Typography>

          {deletionRequested ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Exclusao solicitada. Sua conta sera excluida permanentemente em 30 dias.
              </Alert>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancelDeletion}
                disabled={isLoading}
              >
                Cancelar exclusao
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteForeverIcon />}
            >
              Solicitar exclusao de conta
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Deletion Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle color="error">Confirmar Exclusao de Conta</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Esta acao e irreversivel apos o período de carencia de 30 dias. Todos os
            seus dados serao permanentemente excluidos.
          </DialogContentText>
          <TextField
            fullWidth
            type="password"
            label="Senha"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label='Digite "EXCLUIR MINHA CONTA" para confirmar'
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'EXCLUIR MINHA CONTA'}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleRequestDeletion}
            color="error"
            variant="contained"
            disabled={isLoading || deleteConfirmText !== 'EXCLUIR MINHA CONTA' || !deletePassword}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Excluir conta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
